import { useState } from 'react';
import { TEAM_LABEL } from '../../shared/teams'; // v22 BOLT-105
import { nextRoundStarter, youStartNextRound } from '../../shared/roundStart'; // v29 BOLT-169
import { useGame } from '../state/SocketProvider';
import { Contours } from '../ui/Contours';
import { formatClock } from '../util/format';
import type { PublicState } from '../../shared/protocol';
import type { Standings } from '../../shared/series';

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

/** Score surface — branches on phase: intermission (between rounds) and the
 *  game-end (series winner) are v4; the legacy single round keeps its layout. */
export function Score() {
  const { state } = useGame();
  if (!state) return null;
  if (state.phase === 'intermission') return <Intermission state={state} />;
  if (state.series.totalRounds > 1) return <GameEnd state={state} />;
  return <SingleRoundScore />;
}

/**
 * v18 BOLT-078 — the escape hatch. Between rounds NEITHER player had an exit:
 * the host's only button started another round, everyone else got a "waiting…"
 * line, and the resume token walked a restarted app straight back into it.
 *
 * Deliberately de-emphasized (ghost, bottom-anchored, below the primary CTA —
 * Jake: "deephesized by placing at the bottom") and deliberately TWO-step: the
 * confirm names the consequence, because this ends the game for both players.
 */
function QuitAndExit({ note }: { note: string }) {
  const { send, leave } = useGame();
  const [confirming, setConfirming] = useState(false);
  const quit = () => {
    send({ t: 'leave' }); // server closes the room + signals the other player
    leave(); // forget the token locally, so a relaunch lands on Landing
    location.href = '/';
  };
  return (
    <>
      <button
        className="btn btn--ghost quit-exit"
        data-testid="quit-exit"
        onClick={() => setConfirming(true)}
      >
        Quit and exit
      </button>
      {confirming && (
        <div className="sheet-backdrop" onClick={() => setConfirming(false)} data-testid="quit-confirm">
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet__name">Quit and exit?</div>
            <div className="sheet__desc">{note}</div>
            <div className="sheet__actions">
              <button className="btn" data-testid="quit-cancel" onClick={() => setConfirming(false)}>
                Keep playing
              </button>
              <button className="btn btn--signal" data-testid="quit-confirm-btn" onClick={quit}>
                Quit and exit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// v22 BOLT-105/106: the team name IS the identity, so the old
// `${name} (${team})` shape is redundant twice over, and its `Team A` fallback
// is the very copy the rename retired.
function teamLabel(_state: PublicState, team: 'A' | 'B'): string {
  return TEAM_LABEL[team];
}
/** Has this team hidden in a completed round yet? */
function hasHidden(state: PublicState, team: 'A' | 'B'): boolean {
  return (state.series.history ?? []).some((r) => r.hiderTeam === team);
}
function standingsValue(s: Standings, team: 'A' | 'B', hasHidden = true): string {
  const v = team === 'A' ? s.a : s.b;
  // v24 (Jake): a team that has not hidden yet has NO score. Printing "0m"
  // reads as "they scored nothing", which is a different and wrong claim.
  if (!hasHidden) return '-';
  return s.winCondition === 'mostRounds' ? `${v} ${v === 1 ? 'round' : 'rounds'}` : `${Math.round(v)}m`;
}
const conditionName: Record<Standings['winCondition'], string> = {
  best: 'Best single round',
  cumulative: 'Cumulative',
  mostRounds: 'Most rounds won',
};

function StandingsCard({ state }: { state: PublicState }) {
  const s = state.series.standings;
  return (
    <div className="standings" data-testid="standings">
      <div className="standings__cond">{conditionName[s.winCondition]}</div>
      {(['A', 'B'] as const).map((t) => (
        <div className={`standings__row${s.leader === t ? ' standings__row--lead' : ''}`} key={t} data-testid={`standings-${t}`}>
          <span className="standings__team">{teamLabel(state, t)}</span>
          <span className="standings__val">{standingsValue(s, t, hasHidden(state, t))}</span>
        </div>
      ))}
    </div>
  );
}

function Intermission({ state }: { state: PublicState }) {
  const { send } = useGame();
  const finalMin = state.score?.finalMin ?? 0;
  const ms = state.score?.timeHiddenMs ?? 0;
  const bonusMin = state.score?.bonusMin ?? 0;
  const penaltyMin = state.score?.penaltyMin ?? 0;
  const lastRound = state.series.history[state.series.history.length - 1] ?? null;
  const hiderTeam = lastRound?.hiderTeam ?? null;
  // v29 BOLT-159: roles alternate, so whoever did NOT hide last round hides
  // next - and they are the one who starts it. Derived from the same history
  // the score is derived from, so it cannot drift from what is on screen.
  // v29 BOLT-169: that derivation moved to shared/roundStart.ts, because iOS
  // never got this half and the room DEADLOCKED in the field. It also fixes a
  // second case this inline version had wrong: below the ready-gate floor (and
  // in practice) the server hands the round back to the HOST, and this screen
  // was still offering it to the next hider's team.
  const starter = nextRoundStarter(state);
  const youStart = youStartNextRound(state);
  // snapshotFor withholds `replay` until `ended`; the finished round's GIF
  // rides its history entry instead.
  const replay = lastRound?.replay ?? null;
  const next = () => send({ t: 'start_next_round', idem: uuid() });

  return (
    <div className="screen">
      <Contours />
      <div className="screen__inner">
        <div className="eyebrow score-eyebrow">
          Round {state.series.roundIndex + 1} of {state.series.totalRounds} · {state.code}
        </div>
        <div className="score-h" data-testid="score-headline">
          {hiderTeam ? teamLabel(state, hiderTeam) : 'The hider'} hid for <em>{formatClock(ms)}.</em>
        </div>
        {/* v11 §2H: every game is a series now — the between-rounds screen
            carries the full round story (breakdown + replay), not just a total. */}
        <div className="score-row">
          <span className="k">Hiding time</span>
          <span className="v" data-testid="run-time">
            {formatClock(ms)}
          </span>
        </div>
        {bonusMin > 0 && (
          <div className="score-row">
            <span className="k">+ Bonuses</span>
            <span className="v" data-testid="bonus-line" style={{ color: 'var(--hazard)' }}>
              +{Math.round(bonusMin)}m
            </span>
          </div>
        )}
        {penaltyMin > 0 && (
          <div className="score-row">
            <span className="k">− Penalty</span>
            <span className="v" data-testid="penalty-line" style={{ color: 'var(--signal)' }}>
              −{Math.round(penaltyMin)}m
            </span>
          </div>
        )}
        <div className="score-row total">
          <span className="k">Round score</span>
          <span className="v" data-testid="final-score">
            {formatClock(finalMin * 60_000)}
          </span>
        </div>

        <div className="replay-frame" data-testid="replay">
          {replay ? (
            <img src={replay.gifUrl} alt="Round replay" data-testid="replay-img" />
          ) : (
            <span className="replay-pending">Rendering the replay…</span>
          )}
        </div>
        {replay && (
          <div className="row" style={{ gap: '0.6rem', marginTop: '0.8rem' }}>
            <a className="btn" href={replay.gifUrl} download data-testid="download-gif">
              Download replay
            </a>
            <a className="btn btn--ghost" href={replay.jsonUrl} download data-testid="download-json">
              Event log
            </a>
          </div>
        )}

        <StandingsCard state={state} />

        <div style={{ flex: 1 }} />
        {/* v29 BOLT-159 (Jake): "Upon the start of subsequent rounds, players do
            not need to confirm ready. However the new hider, not the host,
            should see and click the Begin head start button."
            So the control belongs to whoever hides NEXT, which alternates - and
            `series.hiderTeam` is the round that just ended, so the next one is
            the other team. The server enforces the same rule. */}
        {youStart ? (
          <button
            className="btn btn--signal btn--lg"
            style={{ marginTop: '1.2rem' }}
            data-testid="start-next-round"
            onClick={next}
          >
            Begin head start
          </button>
        ) : (
          <div className="eyebrow" style={{ textAlign: 'center', marginTop: '1.2rem' }} data-testid="intermission-wait">
            {starter.kind === 'team'
              ? `Waiting for ${TEAM_LABEL[starter.team]} to start the next round…`
              : starter.kind === 'host'
                ? 'Waiting for the host to start the next round…'
                : 'Waiting for the next round…'}
          </div>
        )}
        {/* v18 BOLT-078: every player can get out from here, host or not. */}
        <QuitAndExit note="This ends the game for both players. The series stops here and you both go back to Home." />
      </div>
    </div>
  );
}

function GameEnd({ state }: { state: PublicState }) {
  const { send } = useGame();
  const s = state.series.standings;
  const playAgain = () => send({ t: 'play_again', idem: uuid() });

  return (
    <div className="screen">
      <Contours />
      <div className="screen__inner">
        <div className="eyebrow score-eyebrow">Game over · {state.code}</div>
        <div className="score-h" data-testid="winner-headline">
          {s.leader === 'tie' ? (
            <>
              <em>Tied.</em>
            </>
          ) : (
            <>
              {s.leader ? teamLabel(state, s.leader) : 'Nobody'} <em>wins.</em>
            </>
          )}
        </div>

        <StandingsCard state={state} />

        <div className="round-breakdown" data-testid="round-breakdown">
          {state.series.history.map((r) => (
            <div className="round-breakdown__row" key={r.roundIndex} data-testid={`round-row-${r.roundIndex}`}>
              <span className="round-breakdown__n">R{r.roundIndex + 1}</span>
              <span className="round-breakdown__team">{teamLabel(state, r.hiderTeam)}</span>
              <span className="round-breakdown__score">{formatClock(r.finalMin * 60_000)}</span>
              {r.replay ? (
                <a className="round-breakdown__replay" href={r.replay.gifUrl} download data-testid={`round-replay-${r.roundIndex}`}>
                  ⤓
                </a>
              ) : (
                <span className="round-breakdown__replay round-breakdown__replay--pending">·</span>
              )}
            </div>
          ))}
        </div>

        <div style={{ flex: 1 }} />
        {/* v15 BOLT-061: the exit says what it does — "New game" read as a
            commitment and trapped anyone who just wanted out. */}
        <div className="row" style={{ gap: '0.6rem', marginTop: '1.2rem' }}>
          {state.you.isHost && (
            <button className="btn btn--signal btn--lg" data-testid="play-again" onClick={playAgain}>
              Play again
            </button>
          )}
        </div>
        {!state.you.isHost && (
          <div className="eyebrow" style={{ textAlign: 'center', marginTop: '0.6rem' }} data-testid="rematch-hint">
            The host can start a rematch
          </div>
        )}
        {/* v18 BOLT-078: the old "Back to Home" only forgot the session on
            THIS device, leaving the other player in a room nobody would ever
            start. It goes through the same confirmed server leave now. */}
        <QuitAndExit note="This closes the game for both players. Standings are already final." />
      </div>
    </div>
  );
}

/** The legacy single-round score (unchanged layout / testids). */
function SingleRoundScore() {
  const { state } = useGame();
  if (!state) return null;
  const caught = state.score?.caught ?? false;
  const ms = state.score?.timeHiddenMs ?? 0;
  const bonusMin = state.score?.bonusMin ?? 0;
  const penaltyMin = state.score?.penaltyMin ?? 0;
  const finalMin = state.score?.finalMin ?? ms / 60_000;
  const replay = state.replay;



  return (
    <div className="screen">
      <Contours />
      <div className="screen__inner">
        <div className="eyebrow score-eyebrow">Run · {state.code}</div>
        <div className="score-h" data-testid="score-headline">
          {caught ? (
            <>
              Caught at <em>{formatClock(ms)}.</em>
            </>
          ) : (
            <>
              Survived. <em>Never found.</em>
            </>
          )}
        </div>

        <div className="score-row">
          <span className="k">Hiding time</span>
          <span className="v" data-testid="run-time">
            {formatClock(ms)}
          </span>
        </div>
        {bonusMin > 0 && (
          <div className="score-row">
            <span className="k">+ Bonuses</span>
            <span className="v" data-testid="bonus-line" style={{ color: 'var(--hazard)' }}>
              +{Math.round(bonusMin)}m
            </span>
          </div>
        )}
        {penaltyMin > 0 && (
          <div className="score-row">
            <span className="k">− Penalty</span>
            <span className="v" data-testid="penalty-line" style={{ color: 'var(--signal)' }}>
              −{Math.round(penaltyMin)}m
            </span>
          </div>
        )}
        <div className="score-row total">
          <span className="k">Final</span>
          <span className="v" data-testid="final-score">
            {formatClock(finalMin * 60_000)}
          </span>
        </div>

        <div className="replay-frame" data-testid="replay">
          {replay ? (
            <img src={replay.gifUrl} alt="Round replay" data-testid="replay-img" />
          ) : (
            <span className="replay-pending">Rendering the replay…</span>
          )}
        </div>

        {replay && (
          <div className="row" style={{ gap: '0.6rem', marginTop: '0.8rem' }}>
            <a className="btn" href={replay.gifUrl} download data-testid="download-gif">
              Download replay
            </a>
            <a className="btn btn--ghost" href={replay.jsonUrl} download data-testid="download-json">
              Event log
            </a>
          </div>
        )}

        <div style={{ flex: 1 }} />
        {/* v15 BOLT-061 named it honestly; v18 BOLT-078 makes it honest at the
            wire too — the room closes instead of being abandoned. */}
        <QuitAndExit note="The game is over. This closes the room and takes you both back to Home." />
      </div>
    </div>
  );
}
